import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Bell, Search, Plus, X, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import useAdvalvas from '../../api/advalvas';
import { useIsAuthenticated, useAuthUser } from 'blitzware-react-sdk';
import { useTranslation } from 'react-i18next';
import Loader from '../ui/loader';
import { useSyncedUser } from '../../context/SyncedUserContext';

// Import components
import PostCard from './PostCard';
import CommentDrawer from './CommentDrawer';
import AddPostModal from './modals/AddPostModal';
import ConfirmationModal from './modals/ConfirmationModal';

// Import utilities
import { DATE_FILTERS, filterCategories, isInDateRange } from '../../lib/utils';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";

// Filter Drawer Component
const FilterDrawer = ({ 
  isOpen, 
  onClose, 
  activeFilter, 
  setActiveFilter, 
  activeDateFilter, 
  setActiveDateFilter,
  searchQuery, 
  setSearchQuery,
  resetFilters 
}) => {
  const { t } = useTranslation();
  // Local state to store values while drawer is open
  const [localFilter, setLocalFilter] = useState(activeFilter);
  const [localDateFilter, setLocalDateFilter] = useState(activeDateFilter);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  
  // Reset local values when drawer opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilter(activeFilter);
      setLocalDateFilter(activeDateFilter);
      setLocalSearchQuery(searchQuery);
    }
  }, [isOpen, activeFilter, activeDateFilter, searchQuery]);
  
  // Handle apply - update parent state with local values
  const handleApply = () => {
    setActiveFilter(localFilter);
    setActiveDateFilter(localDateFilter);
    setSearchQuery(localSearchQuery);
    onClose();
  };
  
  // Handle reset filters
  const handleReset = () => {
    setLocalFilter('all');
    setLocalDateFilter('all');
    setLocalSearchQuery('');
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh]">
        <div className="flex flex-col h-[85vh]">
          <DrawerHeader className="border-b border-neutral-200 dark:border-neutral-700 pb-2">
            <div className="flex items-center justify-between">
              <DrawerTitle className="flex items-center gap-2 text-base">
                <Filter className="h-5 w-5 text-primary" />
                <span>{t('advalvas.filters')}</span>
              </DrawerTitle>
              <DrawerClose className="rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                <X className="h-4 w-4" />
              </DrawerClose>
            </div>
            <DrawerDescription className="mt-1">
              {t('advalvas.refineSearch')}
            </DrawerDescription>
          </DrawerHeader>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
            {/* Category Options */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium mb-2">{t('advalvas.categories')}</h4>
              <div className="grid grid-cols-2 gap-2">
                {filterCategories.map((category) => {
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => setLocalFilter(category.id)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg ${
                        localFilter === category.id
                          ? 'bg-primary text-white'
                          : 'bg-neutral-100 dark:bg-neutral-800'
                      }`}
                    >
                      <category.icon className="w-4 h-4" />
                      <span className="text-sm">
                        {category.id === 'all' 
                          ? t('postTypes.all') 
                          : t(`postTypes.${category.id}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Time Range Options */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium mb-2">{t('advalvas.timeRange')}</h4>
              <div className="grid grid-cols-2 gap-2">
                {DATE_FILTERS.map((filter) => {
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => setLocalDateFilter(filter.id)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg ${
                        localDateFilter === filter.id
                          ? 'bg-primary text-white'
                          : 'bg-neutral-100 dark:bg-neutral-800'
                      }`}
                    >
                      <filter.icon className="w-4 h-4" />
                      <span className="text-sm">{t(`dateFilters.${filter.id}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Search Input */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium mb-2">{t('advalvas.search')}</h4>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('advalvas.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 
                             border border-transparent focus:border-primary focus:ring-2 
                             focus:ring-primary/20 transition-all duration-200"
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            {/* Reset Filters (only if filters are applied) */}
            {(localFilter !== 'all' || localDateFilter !== 'all' || localSearchQuery) && (
              <div className="pt-2">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleReset}
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('advalvas.resetFilters')}
                </Button>
              </div>
            )}
          </div>

          {/* Footer with Apply button */}
          <DrawerFooter className="border-t border-neutral-200 dark:border-neutral-700 pt-3 pb-5">
            <Button
              onClick={handleApply}
              className="w-full"
            >
              {t('advalvas.applyFilters')}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full mt-2">
                {t('common.cancel')}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const AdvalvasPage = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDateFilter, setActiveDateFilter] = useState('all');
  
  const [isPostFormOpen, setIsPostFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activePost, setActivePost] = useState(null);

  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [activeCommentPost, setActiveCommentPost] = useState(null);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  
  // State for mobile filter drawer
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const { getAllPosts, toggleLike, addComment, addPost, updatePost, deletePost, togglePinPost } = useAdvalvas();
  const isAuthenticated = useIsAuthenticated();
  const authUser = useAuthUser();
  const { syncedUserData } = useSyncedUser();
  
  // Use synced user data from backend (complete user info) or fallback to external auth data
  const user = syncedUserData?.user || authUser?.user || authUser;
  const isBuildingManager = user?.role === 2; // Get role from synced user data

  const [lastModified, setLastModified] = useState(Date.now());

  useEffect(() => {
    if (lastModified) {
      fetchPosts();
    }
  }, [lastModified]);

  useEffect(() => {
    fetchPosts();
    
    return () => {
      // Cleanup function to reset modal states when component unmounts
      setIsPostFormOpen(false);
      setIsDeleteConfirmOpen(false);
      setActivePost(null);
      setPostToDelete(null);
      setIsEditMode(false);
    };
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await getAllPosts();
      setPosts(response.posts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const result = await toggleLike(postId, user?.id);
      
      // Update local state after server confirms the change
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            reactions: {
              ...post.reactions,
              likes: result.liked ? post.reactions.likes + 1 : post.reactions.likes - 1,
              isLiked: result.liked 
            }
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const handleAddComment = async (postId, content) => {
    try {
      const newComment = await addComment(postId, content, user?.id);
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            reactions: {
              ...post.reactions,
              comments: [...post.reactions.comments, newComment]
            }
          };
        }
        return post;
      }));

      // Update the active comment post if it's open
      if (activeCommentPost && activeCommentPost.id === postId) {
        setActiveCommentPost({
          ...activeCommentPost,
          reactions: {
            ...activeCommentPost.reactions,
            comments: [...activeCommentPost.reactions.comments, newComment]
          }
        });
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleOpenComments = (post) => {
    setActiveCommentPost(post);
    setIsCommentsOpen(true);
  };

  const handleCloseComments = () => {
    setIsCommentsOpen(false);
  };

  const handleCreatePost = async (postData) => {
    try {
      const newPost = await addPost(postData, user?.id);
      // Ensure the new post has the proper reactions structure
      const formattedPost = {
        ...newPost,
        reactions: {
          likes: 0,
          comments: [],
          isLiked: false,
          ...newPost.reactions // Keep any reactions if they exist
        }
      };
      setPosts([formattedPost, ...posts]);
    } catch (error) {
      console.error('Failed to create post:', error);
      throw error;
    }
  };

  const handleEditPost = async (postData) => {
    try {
      await updatePost(postData, user?.id);
      setLastModified(Date.now()); // Trigger refresh
      await fetchPosts(); // Wacht expliciet op nieuwe data
    } catch (error) {
      console.error('Failed to update post:', error);
    } finally {
      setIsPostFormOpen(false);
      setIsEditMode(false);
      setActivePost(null);
    }
  };

  const handleSubmitPost = async (postData) => {
    try {
      if (isEditMode) {
        await handleEditPost(postData);
      } else {
        await handleCreatePost(postData);
      }
      // Sluit de modal na succesvolle afhandeling
      setIsPostFormOpen(false);
      setActivePost(null);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error submitting post:', error);
    }
  };

  const openEditPostModal = (post) => {
    setActivePost(post);
    setIsEditMode(true);
    setIsPostFormOpen(true);
  };

  const openNewPostModal = () => {
    setActivePost(null);
    setIsEditMode(false);
    setIsPostFormOpen(true);
  };

  const openDeleteConfirmation = (postId) => {
    setPostToDelete(postId);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeletePost = async () => {
    try {
      if (!postToDelete) return;
      await deletePost(postToDelete, user?.id);
      await fetchPosts(); // Wacht op refresh
    } catch (error) {
      console.error('Failed to delete post:', error);
    } finally {
      setIsDeleteConfirmOpen(false);
      setPostToDelete(null);
    }
  };
  
  const handleTogglePin = async (postId, isPinned) => {
    try {
      const result = await togglePinPost(postId, isPinned, user?.id);
    
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            is_pinned: result.is_pinned
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };
  
  const resetFilters = () => {
    setSearchQuery('');
    setActiveFilter('all');
    setActiveDateFilter('all');
  };

  // First sort pinned posts to the top, then by date
  const filteredPosts = posts
    .sort((a, b) => {
      // Sort pinned posts first
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      // Then sort by date
      return new Date(b.created_at) - new Date(a.created_at);
    })
    .filter(post => {
      const matchesCategory = activeFilter === 'all' || post.category === activeFilter;
      const matchesSearch = 
        (post.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (post.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
      const matchesDate = isInDateRange(post.created_at, activeDateFilter);
      return matchesCategory && matchesSearch && matchesDate;
    });

  // Show loading while posts are loading or authentication is being determined
  if (loading || !isAuthenticated || !user) {
    return (
      <div className="flex items-center justify-center h-[calc(100dvh-4rem)]">
        <Loader />
      </div>
    );
  }

  // Count active filters for badge
  const activeFilterCount = [
    activeFilter !== 'all',
    activeDateFilter !== 'all',
    searchQuery.trim() !== ''
  ].filter(Boolean).length;

  return (
    <div className="space-y-4 pb-16 md:pb-6 overflow-y-auto max-h-[calc(100dvh)] px-4 py-6 md:px-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-bold">{t('advalvas.title')}</h1>
        <div className="flex items-center gap-2">
          {/* Mobile filter button */}
          <Button 
            variant="outline" 
            size="icon" 
            className="md:hidden relative"
            onClick={() => setShowMobileFilters(true)}
          >
            <Filter className="h-5 w-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button 
            onClick={openNewPostModal}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('advalvas.newPost')}</span>
          </Button>
        </div>
      </div>
      
      {/* Desktop Filters Card */}
      <Card className="p-4 bg-white dark:bg-neutral-900 hidden md:block">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {filterCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${
                    activeFilter === category.id
                      ? 'bg-primary text-white'
                      : 'bg-neutral-100 dark:bg-neutral-800'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>
                    {category.id === 'all' 
                      ? t('postTypes.all') 
                      : t(`postTypes.${category.id}`)}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {DATE_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveDateFilter(filter.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${
                    activeDateFilter === filter.id
                      ? 'bg-primary text-white'
                      : 'bg-neutral-100 dark:bg-neutral-800'
                  }`}
                >
                  <filter.icon className="w-4 h-4" />
                  <span>{t(`dateFilters.${filter.id}`)}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <input
              type="text"
              placeholder={t('advalvas.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 
                       border border-transparent focus:border-primary focus:ring-2 
                       focus:ring-primary/20 transition-all duration-200"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </Card>
      
      {/* Mobile Filter Drawer */}
      <FilterDrawer 
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        activeDateFilter={activeDateFilter}
        setActiveDateFilter={setActiveDateFilter}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        resetFilters={resetFilters}
      />

      {/* Posts List */}
      {filteredPosts.length > 0 ? (
        <div className="space-y-4">
          {filteredPosts.map((post) => (
            <PostCard 
              key={post.id} 
              post={post} 
              onLike={handleLike}
              onComment={handleAddComment}
              onOpenComments={handleOpenComments}
              onEdit={openEditPostModal}
              onDelete={openDeleteConfirmation}
              onTogglePin={handleTogglePin}
              currentUserId={user?.id}
              isBuildingManager={isBuildingManager}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <Bell className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium">{t('advalvas.noPosts')}</h3>
          <p className="text-muted-foreground mt-2 text-sm px-4">
            {searchQuery || activeFilter !== 'all' || activeDateFilter !== 'all' 
              ? t('advalvas.filterDesc')
              : t('advalvas.noPostsDesc')}
          </p>
          {(searchQuery || activeFilter !== 'all' || activeDateFilter !== 'all') && (
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={resetFilters}
            >
              <X className="w-4 h-4 mr-2" />
              {t('advalvas.resetFilters')}
            </Button>
          )}
        </div>
      )}

      {/* Post Form Dialog */}
      <AddPostModal 
        isOpen={isPostFormOpen}
        onClose={() => setIsPostFormOpen(false)}
        onSubmit={handleSubmitPost}
        post={activePost}
        isEditMode={isEditMode}
      />

      {/* Comments Drawer */}
      <CommentDrawer 
        post={activeCommentPost}
        isOpen={isCommentsOpen}
        onClose={handleCloseComments}
        onAddComment={handleAddComment}
        currentUser={user}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeletePost}
        title={t('deletePost.title')}
        description={t('deletePost.description')}
      />
    </div>
  );
};

export default AdvalvasPage;